/**
 * Role Display Mapping Utility
 *
 * Maps internal role names to user-friendly display names.
 *
 * Every member of the API's UserRole enum appears below, including `partner_admin`/`partner_user`,
 * the earlier spelling still in the enum. A role whose value is shown raw is a missing entry here,
 * not a formatting problem at the call site: no amount of underscore replacement produces a name.
 */

export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  superadmin: 'Super Administrator',
  enterprise_admin: 'Enterprise Admin',
  enterprise_user: 'Enterprise User',
  channel_partner_admin: 'Channel Partner Admin',
  channel_partner_user: 'Channel Partner User',
  partner_admin: 'Partner Admin',
  partner_user: 'Partner User',
  admin: 'Administrator',
  operator: 'Operator',
  staff: 'Staff',
  service_account: 'Service Account'
};

export const ROLE_DESCRIPTIONS: Record<string, string> = {
  superadmin: 'Platform administrator with Management Console access (no clinical access)',
  enterprise_admin: 'Manages the channel partners and organizations of one enterprise',
  enterprise_user: 'Read-only access to one enterprise and the partners beneath it',
  channel_partner_admin: 'Manages the organizations and users of one channel partner',
  channel_partner_user: 'Read-only access to one channel partner',
  partner_admin: 'Earlier spelling of Channel Partner Admin',
  partner_user: 'Earlier spelling of Channel Partner User',
  admin: 'Organization administrator with user management and full clinical access',
  operator: 'Clinical operator with full clinical privileges and recording access',
  staff: 'Read-only clinical access (view patients, recordings, and transcripts)',
  service_account: 'External system API access (automated)'
};

export const ROLE_BADGES: Record<string, { color: string; bg: string }> = {
  superadmin: { color: 'text-purple-800', bg: 'bg-purple-100' },
  enterprise_admin: { color: 'text-sky-800', bg: 'bg-sky-100' },
  enterprise_user: { color: 'text-sky-700', bg: 'bg-sky-50' },
  channel_partner_admin: { color: 'text-violet-800', bg: 'bg-violet-100' },
  channel_partner_user: { color: 'text-violet-700', bg: 'bg-violet-50' },
  partner_admin: { color: 'text-violet-800', bg: 'bg-violet-100' },
  partner_user: { color: 'text-violet-700', bg: 'bg-violet-50' },
  admin: { color: 'text-blue-800', bg: 'bg-blue-100' },
  operator: { color: 'text-green-800', bg: 'bg-green-100' },
  staff: { color: 'text-yellow-800', bg: 'bg-yellow-100' },
  service_account: { color: 'text-indigo-800', bg: 'bg-indigo-100' }
};

/**
 * The role values the filter dropdowns offer, ordered by the label they display, which is the order
 * the grid's Role column sorts in. Shared by both user lists so neither hand-writes options the enum
 * does not have.
 *
 * Three enum values are deliberately absent. `service_account` is an API-key context, not a users
 * row. `partner_admin`/`partner_user` are the earlier spelling of the channel partner roles and
 * carry identical permissions, so offering them lists one authority under two names; a row still
 * storing either spelling is reachable under All Roles only.
 */
export const USER_ROLE_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  'superadmin',
  'enterprise_admin',
  'enterprise_user',
  'channel_partner_admin',
  'channel_partner_user',
  'admin',
  'operator',
  'staff'
]
  .map(value => ({ value, label: ROLE_DISPLAY_NAMES[value] }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Roles a tenant console caller can never see in the platform user list, keyed by the caller's own
 * role. `GET /platform/users` excludes platform staff from both tenant scopes by two forced clauses
 * (`backend/src/utils/userListScope.ts`), and a channel partner's reach is its own organizations plus
 * its partner-scoped users, neither of which holds an enterprise-scoped user. Offering these values
 * offers a filter that answers empty for every caller of that role.
 *
 * A role absent from this map, including an unauthenticated render, is offered the full list: the
 * server decides the result either way, so a guess here must not narrow what an unknown console sees.
 */
const ROLES_HIDDEN_FROM_CALLER: Record<string, ReadonlyArray<string>> = {
  enterprise_admin: ['superadmin'],
  enterprise_user: ['superadmin'],
  channel_partner_admin: ['superadmin', 'enterprise_admin', 'enterprise_user'],
  channel_partner_user: ['superadmin', 'enterprise_admin', 'enterprise_user'],
  partner_admin: ['superadmin', 'enterprise_admin', 'enterprise_user'],
  partner_user: ['superadmin', 'enterprise_admin', 'enterprise_user'],
};

/**
 * The filter options a caller of this role can act on. Order is inherited, so the result stays
 * alphabetical by label.
 */
export const roleFilterOptionsForCaller = (
  callerRole?: string
): ReadonlyArray<{ value: string; label: string }> => {
  const hidden = ROLES_HIDDEN_FROM_CALLER[callerRole ?? ''] ?? [];

  return USER_ROLE_FILTER_OPTIONS.filter(option => !hidden.includes(option.value));
};

/**
 * Get display name for a role
 * @param role - Internal role name (e.g., "operator")
 * @returns User-friendly display name (e.g., "Operator")
 */
export const getRoleDisplayName = (role: string): string => {
  return ROLE_DISPLAY_NAMES[role] || role;
};

/**
 * Get role description
 * @param role - Internal role name
 * @returns Role description text
 */
export const getRoleDescription = (role: string): string => {
  return ROLE_DESCRIPTIONS[role] || 'No description available';
};

/**
 * Get badge styling for a role
 * @param role - Internal role name
 * @returns Tailwind CSS classes for badge
 */
export const getRoleBadgeClasses = (role: string): string => {
  const badge = ROLE_BADGES[role] || { color: 'text-gray-800', bg: 'bg-gray-100' };
  return `${badge.bg} ${badge.color}`;
};

/**
 * Check if role has operator-level access
 * (Used to determine if user can perform recordings)
 * @param role - Internal role name
 * @returns True if role can operate recording equipment
 */
export const isOperatorRole = (role: string): boolean => {
  return ['operator', 'admin'].includes(role);
};

/**
 * Check if role has admin-level access
 * @param role - Internal role name
 * @returns True if role can manage organization
 */
export const isAdminRole = (role: string): boolean => {
  return role === 'admin';
};

/**
 * Check if role has platform management access
 * @param role - Internal role name
 * @returns True if role can access management page
 */
export const isSuperAdminRole = (role: string): boolean => {
  return role === 'superadmin';
};

/**
 * Get all available roles for dropdown
 * @param includeServiceAccount - Include service_account role (default: false)
 * @returns Array of role options with value and label
 */
export const getAvailableRoles = (includeServiceAccount: boolean = false) => {
  const roles = [
    { value: 'staff', label: ROLE_DISPLAY_NAMES.staff },
    { value: 'operator', label: ROLE_DISPLAY_NAMES.operator },
    { value: 'admin', label: ROLE_DISPLAY_NAMES.admin }
  ];

  if (includeServiceAccount) {
    roles.push({ value: 'service_account', label: ROLE_DISPLAY_NAMES.service_account });
  }

  return roles;
};

export default {
  ROLE_DISPLAY_NAMES,
  ROLE_DESCRIPTIONS,
  ROLE_BADGES,
  roleFilterOptionsForCaller,
  getRoleDisplayName,
  getRoleDescription,
  getRoleBadgeClasses,
  isOperatorRole,
  isAdminRole,
  isSuperAdminRole,
  getAvailableRoles
};
